/**
 * PC 多列投影（21n/21o/21q）：`图标竖栏 | 列表栏 | 详情栏 [| 资料面板]`。
 * - 列表栏 = activeHomeTab 的 home 内容（宽度可拖拽、localStorage 记住）；
 * - 详情栏 = **栈顶的详情类路由**（好友详情/群设置/我的各子页…），没有则
 *   activeChatKey 的会话，再没有则空态。
 *
 * **只有少数几类仍然全屏接管**（登录流程/开发页/安装引导/归档/二维码），
 * 名单与理由在 `nav/projection.ts` 的 `DESKTOP_FULLSCREEN`。
 * 21o 之前所有详情页都是全屏接管——点一下好友连左栏列表都没了，
 * 那是「把移动端的页面栈直接搬到桌面」。
 *
 * 21q 再补第四列：**从聊天标题点进去的**群/好友资料不该把聊天替换掉，而是
 * 在聊天右侧再开一栏（人工给图）。判定在 `projection.activePanelRoute`——
 * 同一个目标路由，从通讯录进仍占详情栏，从会话里进才是面板。
 */
import styles from './shell.module.scss';
import {
  activeChatKey,
  activeDetailRoute,
  activeHomeTab,
  activePanelRoute,
  isDesktopFullscreen,
  topRoute,
} from '~/nav/projection';
import type { AppRoute } from '~/nav/routes';
import { useGlobal } from '~/store/useGlobal';
import { useLeftPaneWidth } from './useLeftPaneWidth';
import { ChatPage, EmptyChatPane, HomePage } from './pages';
import { DesktopNavRail } from './DesktopNavRail';
import { LoginPage } from './LoginPage';
import { SetPasswordPage } from './SetPasswordPage';
import { VlistDemoLazy } from '~/list/demo/VlistDemoLazy';
import { StickerPacksLazy } from './StickerPacksLazy';
import { ContactSearchLazy } from './ContactSearchLazy';
import { ContactApplyLazy } from './ContactApplyLazy';
import { FriendDetailLazy } from './FriendDetailLazy';
import { FriendGroupsLazy } from './FriendGroupsLazy';
import { BlacklistLazy } from './BlacklistLazy';
import { AnnouncementsLazy } from './AnnouncementsLazy';
import { CreateGroupLazy } from './CreateGroupLazy';
import { GroupSettingsLazy } from './GroupSettingsLazy';
import { GroupManageLazy } from './GroupManageLazy';
import { GroupMembersLazy } from './GroupMembersLazy';
import { GroupInviteLazy } from './GroupInviteLazy';
import { GroupLogLazy } from './GroupLogLazy';
import { GroupNoticeLazy } from './GroupNoticeLazy';
import { MyProfileLazy } from './MyProfileLazy';
import { MyPrivacyLazy } from './MyPrivacyLazy';
import { RouteSettingsLazy } from './RouteSettingsLazy';
import { MyAppearanceLazy } from './MyAppearanceLazy';
import { MyAccountsLazy } from './MyAccountsLazy';
import { AddAccountPage } from './AddAccountPage';
import { LoginDevicesLazy, TwoFaLazy } from './AccountSecurityLazy';
import { AboutLazy } from './AboutLazy';
import { ArchiveLazy } from './ArchiveLazy';
import { InstallLazy } from './InstallLazy';
import { FeedbackLazy } from './FeedbackLazy';
import { FeedbackHistoryLazy } from './FeedbackHistoryLazy';
import { QrCodeLazy } from './QrCodeLazy';

export function DesktopShell() {
  const nav = useGlobal((s) => s.nav);
  const { width, onDividerPointerDown } = useLeftPaneWidth();

  const top = topRoute(nav);
  // 少数仍然全屏接管的（名单见 projection.DESKTOP_FULLSCREEN）
  if (isDesktopFullscreen(top.kind)) return <FullscreenRoute route={top} />;

  const detail = activeDetailRoute(nav);
  const panel = activePanelRoute(nav);
  const chatKey = activeChatKey(nav);
  return (
    // data-panel 供样式用：窄桌面下面板撑满详情栏、聊天让位（见 shell.module.scss）
    <div className={styles.columns} data-panel={panel !== undefined ? 'true' : undefined}>
      {/* 图标导航竖栏（21n）：桌面独有，挂在列表栏左侧构成三列；
          移动端仍是底部 Tab（HomePage 内自行决定） */}
      <DesktopNavRail active={activeHomeTab(nav)} />
      <aside className={styles.leftPane} data-testid="left-pane" style={{ width: `${width}px` }}>
        <HomePage tab={activeHomeTab(nav)} />
      </aside>
      <div
        className={styles.divider}
        data-testid="pane-divider"
        onPointerDown={onDividerPointerDown}
      />
      <main className={styles.rightPane} data-testid="right-pane">
        {/* 详情优先（21o）：有详情就显示详情，否则回落到会话/空态。
            面板那一支 detail 为 undefined（见 projection），所以聊天留在这 */}
        {detail !== undefined ? (
          <DetailRoute route={detail} />
        ) : chatKey ? (
          <ChatPage conversationKey={chatKey} />
        ) : (
          <EmptyChatPane />
        )}
      </main>
      {/* 第三栏：当前会话的资料面板（21q）。渲染的是**和详情栏同一批组件**，
          它们本来就响应式；面板宽度固定，不参与左栏拖拽 */}
      {panel !== undefined && (
        <aside className={styles.infoPane} data-testid="info-pane">
          <DetailRoute route={panel} />
        </aside>
      )}
    </div>
  );
}

/** 全屏接管的几类（projection.DESKTOP_FULLSCREEN；理由见那里） */
function FullscreenRoute({ route }: { route: AppRoute }) {
  switch (route.kind) {
    case 'login':
      return <LoginPage />;
    case 'set-password':
      return <SetPasswordPage />;
    case 'add-account':
      return <AddAccountPage />;
    case 'dev-vlist':
      return <VlistDemoLazy />;
    case 'install':
      return <InstallLazy />;
    case 'archive':
      return <ArchiveLazy />;
    case 'me-qrcode':
      return <QrCodeLazy />;
    case 'group-qrcode':
      return <QrCodeLazy groupId={route.groupId} />;
    default:
      return null; // isDesktopFullscreen 已筛过，不可达
  }
}

/**
 * 详情栏内容（21o）。**与移动端渲染同一批组件**——这些页本来就是响应式的
 * （移动端占满屏、桌面占右栏），所以这里不需要任何「桌面版」分支。
 */
function DetailRoute({ route }: { route: AppRoute }) {
  switch (route.kind) {
    case 'sticker-packs':
      return <StickerPacksLazy />;
    case 'sticker-pack-edit':
      return <StickerPacksLazy packId={route.packId} />;
    case 'contact-search':
      return <ContactSearchLazy />;
    case 'contact-applies':
      return <ContactApplyLazy kind={route.applyKind} />;
    case 'friend-detail':
      return <FriendDetailLazy friendId={route.friendId} />;
    case 'friend-groups':
      return <FriendGroupsLazy />;
    case 'blacklist':
      return <BlacklistLazy />;
    case 'announcements':
      return <AnnouncementsLazy />;
    case 'create-group':
      return <CreateGroupLazy />;
    case 'group-settings':
      return <GroupSettingsLazy groupId={route.groupId} />;
    case 'group-manage':
      return <GroupManageLazy groupId={route.groupId} />;
    case 'group-members':
      return <GroupMembersLazy groupId={route.groupId} />;
    case 'group-invite':
      return <GroupInviteLazy groupId={route.groupId} />;
    case 'group-log':
      return <GroupLogLazy groupId={route.groupId} />;
    case 'group-notice':
      return <GroupNoticeLazy groupId={route.groupId} />;
    case 'me-profile':
      return <MyProfileLazy />;
    case 'me-privacy':
      return <MyPrivacyLazy />;
    case 'me-appearance':
      return <MyAppearanceLazy />;
    case 'me-accounts':
      return <MyAccountsLazy />;
    case 'me-devices':
      return <LoginDevicesLazy />;
    case 'me-2fa':
      return <TwoFaLazy />;
    case 'me-route':
      return <RouteSettingsLazy />;
    case 'me-about':
      return <AboutLazy />;
    case 'me-feedback':
      return <FeedbackLazy {...(route.target ? { target: route.target } : {})} />;
    case 'me-feedback-history':
      return <FeedbackHistoryLazy />;
    default:
      return null; // activeDetailRoute 已筛过（home/chat/全屏类都不会到这）
  }
}
